import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { Place } from '../types/Place';
import {
  getDateRange,
  getVisiblePlacesAtTime,
  generateMonthMarkers,
  progressToTime,
  timeToProgress,
  getTimelinePlaces,
  formatCurrentDate,
  type MonthMarker,
} from '../utils/timelineUtils';
import { useReducedMotion } from './useReducedMotion';
import { getTheme } from '../config/theme.config';

// Total playback duration sourced from theme config (single source of truth)
const PLAYBACK_DURATION_MS = getTheme().animation.timeMachinePlaybackDuration;

export interface UseTimeMachineResult {
  // Mode state
  isTimeMachineMode: boolean;
  enterTimeMachine: () => void;
  exitTimeMachine: () => void;

  // Timeline state
  currentTime: number; // Ms from epoch
  setCurrentTime: (time: number) => void;
  progress: number; // 0-100%
  setProgress: (progress: number) => void;

  // Playback state
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;

  // Step navigation
  advanceToNextPlace: () => string | null; // Advance timeline to show next place, returns its ID

  // Computed values
  visiblePlaces: Set<string>;
  monthMarkers: MonthMarker[];
  currentDateLabel: string;

  // Date range info
  startMs: number;
  endMs: number;
  durationMs: number;

  // Utility
  hasTimelinePlaces: boolean;
}

export function useTimeMachine(places: Place[]): UseTimeMachineResult {
  const prefersReducedMotion = useReducedMotion();

  // Mode state
  const [isTimeMachineMode, setIsTimeMachineMode] = useState(false);

  // Timeline state
  const [progress, setProgressState] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Animation refs
  const animationRef = useRef<number | null>(null);
  const playbackStartRef = useRef<number | null>(null);
  const playbackStartProgressRef = useRef<number>(0);

  // Calculate date range
  const { startMs, endMs, durationMs } = useMemo(
    () => getDateRange(places),
    [places]
  );

  // Calculate month markers
  const monthMarkers = useMemo(
    () => generateMonthMarkers(places),
    [places]
  );

  // Check if there are timeline-eligible places
  const hasTimelinePlaces = useMemo(
    () => getTimelinePlaces(places).length > 0,
    [places]
  );

  // Calculate current time from progress
  const currentTime = useMemo(
    () => progressToTime(progress, startMs, durationMs),
    [progress, startMs, durationMs]
  );

  // Calculate visible places at current time
  const visiblePlaces = useMemo(
    () => getVisiblePlacesAtTime(places, currentTime),
    [places, currentTime]
  );

  // Format current date for display
  const currentDateLabel = useMemo(
    () => formatCurrentDate(currentTime),
    [currentTime]
  );

  // Set progress (clamped to 0-100)
  const setProgress = useCallback((newProgress: number) => {
    setProgressState(Math.max(0, Math.min(100, newProgress)));
  }, []);

  // Set current time (converts to progress)
  const setCurrentTime = useCallback((timeMs: number) => {
    const newProgress = timeToProgress(timeMs, startMs, durationMs);
    setProgress(newProgress);
  }, [startMs, durationMs, setProgress]);

  // Playback animation tick
  const tick = useCallback((timestamp: number) => {
    if (playbackStartRef.current === null) {
      playbackStartRef.current = timestamp;
    }

    const elapsed = timestamp - playbackStartRef.current;
    // Scale duration based on remaining progress (starting from 50% takes half the time)
    const remainingProgress = 100 - playbackStartProgressRef.current;

    // Guard against division by zero (edge case if starting at 100%)
    if (remainingProgress <= 0) {
      setProgressState(100);
      setIsPlaying(false);
      playbackStartRef.current = null;
      return;
    }

    const scaledDuration = (remainingProgress / 100) * PLAYBACK_DURATION_MS;
    const progressDelta = (elapsed / scaledDuration) * remainingProgress;
    const newProgress = playbackStartProgressRef.current + progressDelta;

    if (newProgress >= 100) {
      // Playback complete
      setProgressState(100);
      setIsPlaying(false);
      playbackStartRef.current = null;
      return;
    }

    setProgressState(newProgress);
    animationRef.current = requestAnimationFrame(tick);
  }, []);

  // Play function
  const play = useCallback(() => {
    if (!hasTimelinePlaces || prefersReducedMotion) {
      // If no places or reduced motion, just jump to end
      setProgressState(100);
      return;
    }

    // If already at end, restart from beginning
    if (progress >= 100) {
      setProgressState(0);
      playbackStartProgressRef.current = 0;
    } else {
      playbackStartProgressRef.current = progress;
    }

    playbackStartRef.current = null;
    setIsPlaying(true);
    animationRef.current = requestAnimationFrame(tick);
  }, [hasTimelinePlaces, prefersReducedMotion, progress, tick]);

  // Pause function
  const pause = useCallback(() => {
    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    playbackStartRef.current = null;
    setIsPlaying(false);
  }, []);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, play, pause]);

  // Enter Time Machine mode
  const enterTimeMachine = useCallback(() => {
    setIsTimeMachineMode(true);
    setProgressState(0);
    setIsPlaying(false);
  }, []);

  // Exit Time Machine mode
  const exitTimeMachine = useCallback(() => {
    pause();
    setIsTimeMachineMode(false);
    setProgressState(0);
  }, [pause]);

  // Advance timeline to show the next place (step-by-step navigation)
  const advanceToNextPlace = useCallback((): string | null => {
    const timelinePlaces = getTimelinePlaces(places);
    if (timelinePlaces.length === 0) return null;

    // Sort places by date
    const sortedPlaces = [...timelinePlaces].sort((a, b) => {
      const dateA = new Date(a.dateSort).getTime();
      const dateB = new Date(b.dateSort).getTime();
      return dateA - dateB;
    });

    // Find the first place that isn't visible yet
    for (const place of sortedPlaces) {
      if (!visiblePlaces.has(place.id)) {
        // Set progress to make this place visible
        const placeTime = new Date(place.dateSort).getTime();
        const newProgress = timeToProgress(placeTime, startMs, durationMs);
        setProgressState(Math.min(100, newProgress + 0.1)); // Small offset to ensure visibility
        return place.id;
      }
    }

    // All places visible, we're at the end
    return null;
  }, [places, visiblePlaces, startMs, durationMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    isTimeMachineMode,
    enterTimeMachine,
    exitTimeMachine,

    currentTime,
    setCurrentTime,
    progress,
    advanceToNextPlace,
    setProgress,

    isPlaying,
    play,
    pause,
    togglePlayPause,

    visiblePlaces,
    monthMarkers,
    currentDateLabel,

    startMs,
    endMs,
    durationMs,

    hasTimelinePlaces,
  };
}
